import type { Balance, TraitId, Unit } from './types';

/**
 * Merge traits. A building's level does more than multiply its numbers: at balance.traitLevels it
 * gains a behaviour (a second barrel, a wider blast, spikes on the wall). That makes "four of the
 * same" a plan rather than an accident, and is the reason to merge on a board where space is scarce.
 */

/** Traits a building has at its level, in the order they were gained. */
export function unitTraits(unit: Pick<Unit, 'arch' | 'level'>, balance: Balance): TraitId[] {
  const out: TraitId[] = [];
  const [first, second] = balance.traits[unit.arch];
  if (unit.level >= balance.traitLevels[0]) out.push(first);
  if (unit.level >= balance.traitLevels[1]) out.push(second);
  return out;
}

/** The trait this building gains next and the level it takes, or null when it has both. */
export function nextTrait(unit: Pick<Unit, 'arch' | 'level'>, balance: Balance): { level: number; trait: TraitId } | null {
  const [first, second] = balance.traits[unit.arch];
  if (unit.level < balance.traitLevels[0]) return { level: balance.traitLevels[0], trait: first };
  if (unit.level < balance.traitLevels[1]) return { level: balance.traitLevels[1], trait: second };
  return null;
}

/** Whether merging up to `level` unlocks a trait: the client celebrates those merges louder. */
export function traitGainedAt(level: number, balance: Balance): boolean {
  return level === balance.traitLevels[0] || level === balance.traitLevels[1];
}
